/**
 * Zigpoll survey page embed
 *
 * Detects when zigpoll fails to render and surfaces the reason.
 * - Shim warning / no live poll → survey closed copy
 * - API error → show zigpoll's error message (unavailable copy as fallback)
 * - API success but empty target → compare pageViews vs openAttempts → view limit
 * - Timeout with no shim signal → generic unavailable message
 */
if (!customElements.get('zigpoll-survey-embed')) {
  class ZigpollSurveyEmbed extends HTMLElement {
    static selectors = {
      target: '.zigpoll-survey-embed__target',
      status: '.zigpoll-survey-embed__status',
      statusTitle: '.zigpoll-survey-embed__status-title',
      statusBody: '.zigpoll-survey-embed__status-body',
    };

    constructor() {
      super();

      this.target = null;
      this.statusEl = null;
      this.statusTitleEl = null;
      this.statusBodyEl = null;
      this.sectionId = '';
      this.pollId = '';
      this.embedTargetId = '';
      this.closeEventName = 'zigpoll-survey:close';
      this.closeRedirectUrl = '';
      this.loadTimeoutMs = 12000;
      this.sdkOnloadFired = false;
      this.abortController = null;
      this.observer = null;
      this.loadTimeoutTimer = null;
      this.inspectTimer = null;
      this.closeFallbackTimer = null;
      this.closeHandler = null;
      this.hasLoadedContent = false;
      this.hasClosed = false;
      this.hasUnavailable = false;
      this.pendingCloseReason = '';
      this.pendingCloseAt = 0;
      this.connectedAt = 0;
      this.networkProbeActive = false;
      this.originalFetch = null;
      this.originalXHROpen = null;
      this.originalXHRSend = null;
      this.shimData = null;
      this.closedTitle = '';
      this.closedBody = '';
      this.unavailableTitle = '';
      this.unavailableBody = '';

      this.onTargetMutate = this.onTargetMutate.bind(this);
      this.onClose = this.onClose.bind(this);
      this.checkCloseFallback = this.checkCloseFallback.bind(this);
      this.onLoadTimeout = this.onLoadTimeout.bind(this);
    }

    connectedCallback() {
      if (this.abortController) return;

      this.target = this.querySelector(ZigpollSurveyEmbed.selectors.target);
      this.statusEl = this.querySelector(ZigpollSurveyEmbed.selectors.status);
      this.statusTitleEl = this.querySelector(ZigpollSurveyEmbed.selectors.statusTitle);
      this.statusBodyEl = this.querySelector(ZigpollSurveyEmbed.selectors.statusBody);
      if (!this.target) return;

      this.connectedAt = Date.now();
      this.sectionId = this.dataset.sectionId || '';
      this.pollId = this.dataset.pollId || '';
      this.embedTargetId = this.target.id || '';
      this.closeEventName = this.dataset.closeEventName || 'zigpoll-survey:close';
      this.closeRedirectUrl = this.dataset.closeRedirectUrl || '';
      this.loadTimeoutMs = Math.max(3000, (Number(this.dataset.loadTimeoutS) || 12) * 1000);
      this.closedTitle = (this.dataset.closedTitle || '').trim();
      this.closedBody = (this.dataset.closedBody || '').trim();
      this.unavailableTitle = (this.dataset.unavailableTitle || '').trim();
      this.unavailableBody = (this.dataset.unavailableBody || '').trim();

      this.abortController = new AbortController();

      this.setupNetworkProbe();
      this.setupZigpollOnloadHook();
      this.setupLoadObserver();
      this.setupLoadTimeout();
      this.setupCloseBridge();
      this.registerCloseHandler();
    }

    disconnectedCallback() {
      this.unregisterCloseHandler();
      this.teardownNetworkProbe();
      this.teardownLoadObserver();
      this.teardownCloseFallback();
      this.teardownLoadTimeout();
      this.teardownInspectTimer();

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Network probe — intercept zigpoll API responses                    */
    /* ------------------------------------------------------------------ */

    setupNetworkProbe() {
      if (this.networkProbeActive) return;

      this.#probeFetch();
      this.#probeXHR();
      this.networkProbeActive = true;
    }

    teardownNetworkProbe() {
      if (!this.networkProbeActive) return;

      if (this.originalFetch) {
        window.fetch = this.originalFetch;
        this.originalFetch = null;
      }
      if (this.originalXHROpen) {
        XMLHttpRequest.prototype.open = this.originalXHROpen;
        this.originalXHROpen = null;
      }
      if (this.originalXHRSend) {
        XMLHttpRequest.prototype.send = this.originalXHRSend;
        this.originalXHRSend = null;
      }

      this.networkProbeActive = false;
    }

    #probeFetch() {
      const self = this;
      this.originalFetch = window.fetch;

      window.fetch = function (input, init) {
        const url = self.#resolveUrl(input);
        const isZigpoll = self.#isZigpollApiUrl(url);

        return self.originalFetch.call(window, input, init).then((res) => {
          if (!isZigpoll || self.hasLoadedContent || self.hasUnavailable) return res;

          const cloned = res.clone();
          cloned.text().then((body) => {
            self.#handleApiResponse(url, res.status, body);
          }).catch(() => {});

          return res;
        });
      };
    }

    #probeXHR() {
      const self = this;
      this.originalXHROpen = XMLHttpRequest.prototype.open;
      this.originalXHRSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__zigpollUrl = url;
        this.__zigpollIsApi = self.#isZigpollApiUrl(String(url || ''));
        return self.originalXHROpen.call(this, method, url, ...rest);
      };

      XMLHttpRequest.prototype.send = function (...args) {
        if (this.__zigpollIsApi && !self.hasLoadedContent && !self.hasUnavailable) {
          this.addEventListener('load', function () {
            self.#handleApiResponse(
              String(this.__zigpollUrl || ''),
              this.status,
              this.responseText
            );
          });
        }
        return self.originalXHRSend.call(this, ...args);
      };
    }

    #resolveUrl(input) {
      if (typeof input === 'string') return input;
      if (input instanceof URL) return input.href;
      if (input && typeof input.url === 'string') return input.url;
      return '';
    }

    #isZigpollApiUrl(url) {
      return /zigpoll\.com\//i.test(url) && !/\.js(\?|$)/i.test(url);
    }

    #handleApiResponse(url, status, body) {
      let data = null;

      try {
        data = JSON.parse(body);
        if (data && (data.poll || data.warning || data.error)) {
          this.shimData = data;
        }
      } catch (e) { /* not JSON */ }

      if (status >= 400) {
        const errorMsg = (data && (data.error || data.message)) || '';
        this.showUnavailable('api_error', { message: errorMsg || 'Server error (HTTP ' + status + ')' });
        return;
      }

      if (data && data.error) {
        this.showUnavailable('api_error', { message: String(data.error) });
        return;
      }

      if (this.#isShimClosedResponse(data)) {
        this.showUnavailable('survey_closed');
      }
    }

    /**
     * Zigpoll returns a warning (and skips embed.js) when the poll is not live.
     * @param {object|null} data
     * @returns {boolean}
     */
    #isShimClosedResponse(data) {
      if (!data || data.poll || !data.warning) return false;
      const exitIntentPolls = data.exitIntentPolls;
      return !(Array.isArray(exitIntentPolls) && exitIntentPolls.length > 0);
    }

    /**
     * Zigpoll calls onload(message) for shim errors/warnings, and onload() on success.
     * @param {string} message
     * @returns {'survey_closed'|'api_error'}
     */
    #classifySdkMessage(message) {
      if (/no poll found/i.test(message)) return 'survey_closed';
      return 'api_error';
    }

    /* ------------------------------------------------------------------ */
    /*  Zigpoll onload hook                                                */
    /* ------------------------------------------------------------------ */

    setupZigpollOnloadHook() {
      window.Zigpoll = window.Zigpoll || {};

      const previousOnload = window.Zigpoll.onload;
      window.Zigpoll.onload = (...args) => {
        this.sdkOnloadFired = true;
        if (typeof previousOnload === 'function') {
          previousOnload(...args);
        }

        const rawMessage = args[0];
        const sdkMessage = typeof rawMessage === 'string'
          ? rawMessage.trim()
          : (rawMessage && rawMessage.message ? String(rawMessage.message).trim() : '');

        if (sdkMessage && !this.hasLoadedContent && !this.hasUnavailable && !this.hasClosed) {
          this.showUnavailable(this.#classifySdkMessage(sdkMessage), { message: sdkMessage });
          return;
        }

        this.scheduleInspectContent();
      };
    }

    scheduleInspectContent() {
      if (this.inspectTimer) return;
      this.inspectTimer = window.setTimeout(() => {
        this.inspectTimer = null;
        this.inspectContent();
      }, 2000);
    }

    teardownInspectTimer() {
      if (this.inspectTimer) {
        window.clearTimeout(this.inspectTimer);
        this.inspectTimer = null;
      }
    }

    inspectContent() {
      if (this.hasLoadedContent || this.hasUnavailable || this.hasClosed) return;

      // Survey rendered normally — iframe present
      if (this.target.querySelector('iframe')) {
        this.markLoaded();
        return;
      }

      // Check if user already submitted in a previous session
      if (this.#hasAlreadySubmitted()) {
        this.showUnavailable('already_submitted');
        return;
      }

      if (this.#isShimClosedResponse(this.shimData)) {
        this.showUnavailable('survey_closed');
        return;
      }

      // Target empty — check if view limit was hit
      const pageViews = this.#getPageViews();
      const openAttempts = parseInt(this.shimData?.poll?.visibilitySettings?.openAttempts, 10) || 0;

      if (pageViews > 0 && openAttempts > 0 && pageViews >= openAttempts) {
        this.showUnavailable('frequency_limit');
        return;
      }

      // No iframe but has other content — treat as loaded
      if (this.hasRenderableContent()) {
        this.markLoaded();
      }
    }

    #getPageViews() {
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key || !key.toLowerCase().includes('zigpoll')) continue;
          try {
            const parsed = JSON.parse(localStorage.getItem(key) || '');
            if (parsed && parsed.data) {
              const pv = parseInt(parsed.data['zigpoll-page-views'], 10);
              if (!isNaN(pv)) return pv;
            }
          } catch (e) { /* not JSON */ }
        }
      } catch (e) { /* inaccessible */ }
      return 0;
    }

    #hasAlreadySubmitted() {
      try {
        return localStorage.getItem('zp-embed-submitted-' + this.pollId) === '1';
      } catch (e) {
        return false;
      }
    }

    /* ------------------------------------------------------------------ */
    /*  MutationObserver                                                   */
    /* ------------------------------------------------------------------ */

    setupLoadObserver() {
      this.teardownLoadObserver();
      this.observer = new MutationObserver(this.onTargetMutate);
      this.observer.observe(this.target, { childList: true, subtree: true });
    }

    teardownLoadObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    onTargetMutate() {
      if (this.hasLoadedContent || this.hasUnavailable || this.hasClosed) return;
      if (!this.hasRenderableContent()) return;
      if (this.sdkOnloadFired) {
        this.inspectContent();
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Load timeout                                                       */
    /* ------------------------------------------------------------------ */

    setupLoadTimeout() {
      this.teardownLoadTimeout();
      this.loadTimeoutTimer = window.setTimeout(this.onLoadTimeout, this.loadTimeoutMs);
    }

    teardownLoadTimeout() {
      if (this.loadTimeoutTimer) {
        window.clearTimeout(this.loadTimeoutTimer);
        this.loadTimeoutTimer = null;
      }
    }

    onLoadTimeout() {
      if (this.hasLoadedContent || this.hasUnavailable || this.hasClosed) return;

      // Last checks before falling through to timeout
      if (this.#hasAlreadySubmitted()) {
        this.showUnavailable('already_submitted');
        return;
      }

      if (this.#isShimClosedResponse(this.shimData)) {
        this.showUnavailable('survey_closed');
        return;
      }

      const pageViews = this.#getPageViews();
      const openAttempts = parseInt(this.shimData?.poll?.visibilitySettings?.openAttempts, 10) || 0;

      if (pageViews > 0 && openAttempts > 0 && pageViews >= openAttempts) {
        this.showUnavailable('frequency_limit');
        return;
      }

      this.showUnavailable('timeout');
    }

    /* ------------------------------------------------------------------ */
    /*  Content helpers                                                    */
    /* ------------------------------------------------------------------ */

    hasRenderableContent() {
      if (!this.target) return false;
      return Boolean(this.target.children.length || this.target.textContent.trim());
    }

    markLoaded() {
      if (this.hasUnavailable || this.hasLoadedContent) return;

      this.hasLoadedContent = true;
      this.classList.add('is-loaded');
      this.teardownNetworkProbe();
      this.teardownLoadObserver();
      this.teardownLoadTimeout();
      this.teardownInspectTimer();
      this.setupCloseFallback();
    }

    /* ------------------------------------------------------------------ */
    /*  Unavailable state                                                  */
    /* ------------------------------------------------------------------ */

    showUnavailable(reason, meta = {}) {
      if (this.hasUnavailable || this.hasLoadedContent || this.hasClosed) return;

      this.hasUnavailable = true;
      this.teardownNetworkProbe();
      this.teardownLoadObserver();
      this.teardownLoadTimeout();
      this.teardownInspectTimer();

      const { title, body } = this.#getCopyForReason(reason, meta);

      if (this.statusTitleEl) this.statusTitleEl.textContent = title;
      if (this.statusBodyEl) this.statusBodyEl.textContent = body;
      if (this.statusEl) this.statusEl.hidden = false;

      this.classList.add('is-unavailable');
      this.classList.add('is-loaded');

      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'zigpoll_survey_embed_unavailable',
          sectionId: this.sectionId,
          pollId: this.pollId,
          reason,
        });
      }
    }

    #getCopyForReason(reason, meta) {
      const unavailableTitle = this.unavailableTitle || 'Survey unavailable';
      const unavailableBody = this.unavailableBody
        || 'Loading timeout. Please open the form in a incognito browser or on another device.';

      switch (reason) {
        case 'survey_closed':
          return {
            title: this.closedTitle || 'This survey has ended',
            body: this.closedBody
              || 'Thank you for your interest. This survey is no longer accepting responses.',
          };

        case 'api_error':
          return {
            title: unavailableTitle,
            body: meta.message || unavailableBody,
          };

        case 'frequency_limit':
          return {
            title: unavailableTitle,
            body: 'View limit reached. Please open the form in a incognito browser or on another device.',
          };

        case 'already_submitted':
          return {
            title: 'Survey submitted',
            body: 'You have already submitted this survey. Thank you for your participation.',
          };

        default:
          return {
            title: unavailableTitle,
            body: unavailableBody,
          };
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Close bridge                                                       */
    /* ------------------------------------------------------------------ */

    setupCloseBridge() {
      window.Zigpoll = window.Zigpoll || {};
      window.__zigpollSurveyEmbedCloseHandlers = window.__zigpollSurveyEmbedCloseHandlers || [];

      if (window.__zigpollSurveyEmbedOnClosePatched) return;

      const previousOnClose = window.Zigpoll.onClose;

      window.Zigpoll.onClose = (payload) => {
        if (typeof previousOnClose === 'function') {
          previousOnClose(payload);
        }
        (window.__zigpollSurveyEmbedCloseHandlers || []).forEach((handler) => {
          if (typeof handler === 'function') handler(payload);
        });
      };

      window.__zigpollSurveyEmbedOnClosePatched = true;
    }

    registerCloseHandler() {
      this.unregisterCloseHandler();
      this.closeHandler = this.onClose;
      window.__zigpollSurveyEmbedCloseHandlers.push(this.closeHandler);
    }

    unregisterCloseHandler() {
      if (!this.closeHandler || !window.__zigpollSurveyEmbedCloseHandlers) return;
      window.__zigpollSurveyEmbedCloseHandlers = window.__zigpollSurveyEmbedCloseHandlers.filter(
        (handler) => handler !== this.closeHandler
      );
      this.closeHandler = null;
    }

    setupCloseFallback() {
      if (this.closeFallbackTimer || this.hasClosed) return;
      this.closeFallbackTimer = window.setInterval(this.checkCloseFallback, 500);
    }

    teardownCloseFallback() {
      if (this.closeFallbackTimer) {
        window.clearInterval(this.closeFallbackTimer);
        this.closeFallbackTimer = null;
      }
      this.pendingCloseReason = '';
      this.pendingCloseAt = 0;
    }

    getCloseFallbackReason() {
      if (!this.target || !this.hasLoadedContent) return '';

      const targetStyles = window.getComputedStyle(this.target);
      if (targetStyles.display === 'none' || targetStyles.visibility === 'hidden') {
        return 'target-hidden';
      }

      if (!this.hasRenderableContent()) return 'target-empty';

      const iframes = Array.from(this.target.querySelectorAll('iframe'));
      if (!iframes.length) return '';

      const hasVisibleIframe = iframes.some((iframe) => {
        const iframeStyles = window.getComputedStyle(iframe);
        return iframeStyles.display !== 'none' && iframeStyles.visibility !== 'hidden';
      });

      if (!hasVisibleIframe) return 'iframe-hidden';
      return '';
    }

    checkCloseFallback() {
      if (this.hasClosed || !this.isConnected || this.hasUnavailable) return;

      const closeReason = this.getCloseFallbackReason();
      if (!closeReason) {
        this.pendingCloseReason = '';
        this.pendingCloseAt = 0;
        return;
      }

      if (this.pendingCloseReason !== closeReason) {
        this.pendingCloseReason = closeReason;
        this.pendingCloseAt = Date.now();
        return;
      }

      if (Date.now() - this.pendingCloseAt < 1500) return;
      this.onClose({ source: 'fallback', reason: closeReason });
    }

    onClose(payload) {
      if (this.hasClosed || this.hasUnavailable) return;

      // Detect real submission (zigpoll passes response array) and persist the flag.
      // Our fallback passes {source:'fallback'}, never an array.
      if (Array.isArray(payload) && payload.length > 0) {
        try { localStorage.setItem('zp-embed-submitted-' + this.pollId, '1'); } catch (e) {}
      }

      this.hasClosed = true;
      this.classList.add('is-loaded');
      this.teardownCloseFallback();
      this.teardownLoadTimeout();
      this.teardownInspectTimer();
      this.teardownNetworkProbe();

      window.dispatchEvent(new CustomEvent(this.closeEventName, {
        detail: { sectionId: this.sectionId, pollId: this.pollId, payload: payload || null },
      }));

      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'zigpoll_survey_embed_close',
          sectionId: this.sectionId,
          pollId: this.pollId,
        });
      }

      this.navigateBack();
    }

    navigateBack() {
      if (window.Shopify?.designMode) return;

      if (this.closeRedirectUrl) {
        window.location.assign(this.closeRedirectUrl);
        return;
      }

      const referrer = document.referrer;
      if (referrer && referrer !== window.location.href) {
        window.location.assign(referrer);
        return;
      }

      window.location.assign('/');
    }
  }

  customElements.define('zigpoll-survey-embed', ZigpollSurveyEmbed);
}
